import type { OverridedMixpanel } from "mixpanel-browser"

import mixpanel from "mixpanel-browser"

import { CONFIG } from "../config-global"

import { getOrCreateCandidateSessionId } from "./candidateSessionId"

declare global {
    interface Window {
        mixpanel: OverridedMixpanel
    }
}

/** Internal batch queue (not on Mixpanel typings); used to flush before navigation */
type MixpanelWithBatchers = typeof mixpanel & {
    request_batchers?: { events?: { flush: (opts?: { unloading?: boolean }) => Promise<unknown> } }
}

if (typeof window !== "undefined") {
    window.mixpanel = mixpanel
}

const MERGED_CANDIDATE_IDS_KEY = "onlyround_mixpanel_merged_candidate_ids"

let currentCandidateId: string | null = null

function getMixpanelCookieDomain(): string | undefined {
    if (typeof window === "undefined") return undefined
    const h = window.location.hostname
    if (h === "localhost" || h.endsWith(".local")) return undefined
    if (h.endsWith(".onlyround.ai") || h === "onlyround.ai") return ".onlyround.ai"
    if (h.endsWith(".apna.co") || h === "apna.co") return ".apna.co"
    return undefined
}

// Cross-app stitching: enable "ID Merge" / identity merge on the Mixpanel project (Project Settings → Identity Merge).
if (CONFIG.site.MIXPANEL_KEY) {
    const cookieDomain = getMixpanelCookieDomain()
    mixpanel.init(CONFIG.site.MIXPANEL_KEY, {
        api_host: CONFIG.site.MIXPANEL_PROXY_URL,
        debug: CONFIG.site.env !== "production",
        // Cookie + cross-subdomain shares the same anonymous distinct_id across *.onlyround.ai (localStorage does not).
        persistence: "cookie",
        cross_subdomain_cookie: true,
        ...(cookieDomain ? { cookie_domain: cookieDomain } : {}),
    })
}

export const analyticsCommonProperties = {}

const getUpdatedExtraData = () => {
    return analyticsCommonProperties
}

export interface CommonEventProperties {
    workspaceId?: string
    workspaceName?: string
    companyName?: string
    roundId?: string
    jobTitle?: string
    jobId?: string
    sessionId?: string | null
    [key: string]: any
}

/**
 * Links the current anonymous distinct_id to this candidate (once per id), then identifies.
 * Call after candidate_id is known (OTP verify and/or walk-in response).
 */
const mergeAnonymousToCandidateIdentity = (candidateId: string) => {
    if (!candidateId) return
    let merged: string[] = []
    try {
        merged = JSON.parse(localStorage.getItem(MERGED_CANDIDATE_IDS_KEY) || "[]")
    } catch {
        merged = []
    }
    if (!merged.includes(candidateId)) {
        mixpanel.alias(candidateId)
        merged.push(candidateId)
        try {
            localStorage.setItem(MERGED_CANDIDATE_IDS_KEY, JSON.stringify(merged))
        } catch {
            // ignore
        }
    }
    mixpanel.identify(candidateId)
    currentCandidateId = candidateId
}

const actions = {
    identify: (id: string) => {
        mixpanel.identify(id)
        currentCandidateId = id
    },

    mergeAnonymousToCandidateIdentity,

    /**
     * Register common properties that will be AUTOMATICALLY included with EVERY track() event
     * These properties persist across all events until cleared or updated
     *
     * @example
     * // Set common properties once
     * Mixpanel.register({
     *   workspaceId: "123",
     *   companyName: "Acme Corp",
     *   jobTitle: "Software Engineer"
     * })
     *
     * // Now every track call automatically includes these properties
     * Mixpanel.track("resume_uploaded") // Includes workspaceId, companyName, jobTitle
     * Mixpanel.track("form_submitted", { formType: "application" }) // Also includes common properties
     */
    register: (props: CommonEventProperties) => {
        mixpanel.register(props)
    },

    /**
     * Alias for register() - Register common properties that will be automatically included with every event
     */
    registerCommonProperties: (props: CommonEventProperties) => {
        mixpanel.register(props)
    },

    /**
     * Unregister specific properties so they won't be sent with future events
     *
     * @example
     * Mixpanel.unregister("roundId") // Remove roundId from future events
     */
    unregister: (propertyName: string) => {
        mixpanel.unregister(propertyName)
    },

    alias: (id: string) => {
        mixpanel.alias(id)
    },

    /**
     * Track an event. Automatically includes properties registered via register() or registerCommonProperties()
     *
     * @param name - Event name (will be prefixed with "onlyround_")
     * @param props - Additional properties specific to this event (optional)
     *
     * @example
     * // Track with just event name (includes registered common properties)
     * Mixpanel.track("button_clicked")
     *
     * // Track with additional properties (merged with common properties)
     * Mixpanel.track("resume_uploaded", { fileSize: 1024, fileType: "pdf" })
     */
    track: (name: string, props?: any) => {
        const { candidate_id: propCid, sessionId: _sessionIdProp, ...rest } = props || {}
        mixpanel.track(`onlyround_${name}`, {
            ...rest,
            ...getUpdatedExtraData(),
            candidate_id: currentCandidateId ?? propCid ?? null,
            sessionId: getOrCreateCandidateSessionId() ?? null,
        })
    },

    getGroup: (name: string, value: any) => {
        return mixpanel.get_group(name, value)
    },
    people: {
        set: (props: any) => {
            mixpanel.people.set(props)
        },
        set_once: (props: any) => {
            mixpanel.people.set_once(props)
        },
        union: (key: string, val: any) => {
            mixpanel.people.union(key, val)
        },
    },
    reset: () => {
        currentCandidateId = null
        return mixpanel.reset()
    },

    /**
     * Sends queued alias/identify/track requests before full-page navigation.
     * mixpanel-browser does not expose `mixpanel.flush()` on the public API — the queue lives on
     * `request_batchers.events.flush`. `{ unloading: true }` uses sendBeacon where supported.
     */
    flushThen: (callback: () => void) => {
        const eventsBatcher = (mixpanel as MixpanelWithBatchers).request_batchers?.events
        if (eventsBatcher && typeof eventsBatcher.flush === "function") {
            void eventsBatcher
                .flush({ unloading: true })
                .catch(() => undefined)
                .finally(() => {
                    callback()
                })
        } else {
            setTimeout(callback, 0)
        }
    },
}

const Mixpanel = actions

export default Mixpanel
